import type { AnswerState, AnswerValue } from '@repo/contracts/cardfinder/funnel/types';
import type {
  BalanceTransferRecommendationsInput,
  BtPriority,
  BtRewardsImportance,
  BuildingCreditRecommendationsInput,
  CashbackRecommendationsInput,
  CobrandAirline,
  CobrandHotel,
  Perk,
  SavingInterestPriority,
  SavingOnInterestRecommendationsInput,
  SpendingCategory,
  TravelRecommendationsInput,
  UserCreditTier,
} from './graphql/__generated__/graphql';

// Every value below is checked by the compiler against the literal string-union
// type generated from schema.graphql (see plan 2's `enumsAsTypes: true`). If the
// subgraph schema renames or removes an enum value, this file fails to typecheck.

const USER_CREDIT_TIER_MAP: Record<string, UserCreditTier> = {
  excellent: 'EXCELLENT',
  good: 'GOOD',
  fair: 'FAIR',
  poor: 'POOR',
  not_sure: 'NOT_SURE',
  no_history: 'NO_HISTORY',
};

const SPENDING_CATEGORY_MAP: Record<string, SpendingCategory> = {
  travel: 'TRAVEL',
  hotel: 'HOTEL',
  dining: 'DINING',
  groceries: 'GROCERIES',
  gas: 'GAS',
  entertainment: 'ENTERTAINMENT',
  online_shopping: 'ONLINE_SHOPPING',
  home_improvement: 'HOME_IMPROVEMENT',
  streaming: 'STREAMING',
};

const PERK_MAP: Record<string, Perk> = {
  'has-companion-fare-annual-flight': 'HAS_COMPANION_FARE_ANNUAL_FLIGHT',
  'has-free-annual-hotel-night': 'HAS_FREE_ANNUAL_HOTEL_NIGHT',
  'has-free-checked-bags': 'HAS_FREE_CHECKED_BAGS',
  'has-airport-lounge-access': 'HAS_AIRPORT_LOUNGE_ACCESS',
  'has-globalentry-tsa-credit': 'HAS_GLOBALENTRY_TSA_CREDIT',
  'has-primary-rental-car-insurance': 'HAS_PRIMARY_RENTAL_CAR_INSURANCE',
  'has-travel-credits': 'HAS_TRAVEL_CREDITS',
  'has-automatic-elite-status': 'HAS_AUTOMATIC_ELITE_STATUS',
};

// CobrandAirline and CobrandHotel both include OTHER. If loyalty_programs ever offered
// 'OTHER' as a value, it would map into both airlineLoyaltyPrograms and
// hotelLoyaltyPrograms below. This is currently unreachable because the funnel config
// never offers OTHER as a loyalty-program option.
const COBRAND_AIRLINE_MAP: Record<string, CobrandAirline> = {
  AEROPLAN: 'AEROPLAN',
  AIR_LINGUS: 'AIR_LINGUS',
  ALASKA_AIRLINES: 'ALASKA_AIRLINES',
  AMERICAN_AIRLINES: 'AMERICAN_AIRLINES',
  BRITISH_AIRWAYS: 'BRITISH_AIRWAYS',
  DELTA: 'DELTA',
  IBERIA: 'IBERIA',
  JETBLUE: 'JETBLUE',
  OTHER: 'OTHER',
  SOUTHWEST_AIRLINES: 'SOUTHWEST_AIRLINES',
  UNITED_AIRLINES: 'UNITED_AIRLINES',
};

const COBRAND_HOTEL_MAP: Record<string, CobrandHotel> = {
  BEST_WESTERN: 'BEST_WESTERN',
  CHOICE: 'CHOICE',
  HILTON: 'HILTON',
  HYATT: 'HYATT',
  IHG: 'IHG',
  MARRIOTT: 'MARRIOTT',
  OTHER: 'OTHER',
  RADISSON: 'RADISSON',
  WYNDHAM: 'WYNDHAM',
};

const BT_PRIORITY_MAP: Record<string, BtPriority> = {
  longer_period: 'LONGER_PERIOD',
  lower_fee: 'LOWER_FEE',
  not_sure: 'NOT_SURE',
};

const BT_REWARDS_IMPORTANCE_MAP: Record<string, BtRewardsImportance> = {
  rewards_not_important: 'REWARDS_NOT_IMPORTANT',
  open_to_rewards: 'OPEN_TO_REWARDS',
  not_sure: 'NOT_SURE',
};

const SAVING_INTEREST_PRIORITY_MAP: Record<string, SavingInterestPriority> = {
  longest_period: 'LONGEST_PERIOD',
  open_to_rewards: 'OPEN_TO_REWARDS',
  not_sure: 'NOT_SURE',
};

function isString(value: AnswerValue | undefined): value is string {
  return typeof value === 'string';
}

function isNumber(value: AnswerValue | undefined): value is number {
  return typeof value === 'number';
}

function isStringArray(value: AnswerValue | undefined): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

function mapDefined<T>(values: string[], map: Record<string, T>): T[] {
  return values.map(value => map[value]).filter((value): value is T => value !== undefined);
}

function toUserCreditTier(answers: AnswerState): UserCreditTier | undefined {
  return isString(answers.credit_score) ? USER_CREDIT_TIER_MAP[answers.credit_score] : undefined;
}

function toOwnedCardIds(answers: AnswerState) {
  return isStringArray(answers.current_cards) && answers.current_cards.length > 0
    ? answers.current_cards
    : undefined;
}

function toSpendingCategories(answers: AnswerState): SpendingCategory[] {
  if (!isStringArray(answers.spend_categories)) return [];
  return mapDefined(answers.spend_categories, SPENDING_CATEGORY_MAP);
}

function toPerks(answers: AnswerState): Perk[] {
  if (!isStringArray(answers.perks)) return [];
  return mapDefined(answers.perks, PERK_MAP);
}

function toHotelLoyaltyPrograms(answers: AnswerState): CobrandHotel[] {
  if (!isStringArray(answers.loyalty_programs)) return [];
  return mapDefined(answers.loyalty_programs, COBRAND_HOTEL_MAP);
}

function toAirlineLoyaltyPrograms(answers: AnswerState): CobrandAirline[] {
  if (!isStringArray(answers.loyalty_programs)) return [];
  return mapDefined(answers.loyalty_programs, COBRAND_AIRLINE_MAP);
}

export function toTravelInput(answers: AnswerState): TravelRecommendationsInput {
  const input: TravelRecommendationsInput = {
    spendingCategories: toSpendingCategories(answers),
    perks: toPerks(answers),
    hotelLoyaltyPrograms: toHotelLoyaltyPrograms(answers),
    airlineLoyaltyPrograms: toAirlineLoyaltyPrograms(answers),
  };

  const userCreditScore = toUserCreditTier(answers);
  if (userCreditScore) input.userCreditScore = userCreditScore;

  const ownedCardIds = toOwnedCardIds(answers);
  if (ownedCardIds) input.ownedCardIds = ownedCardIds;

  const monthlySpend = answers.monthly_spend;
  if (isNumber(monthlySpend)) input.monthlySpend = monthlySpend;

  if (answers.annual_fee === 'no') input.willingToPayAnnualFee = false;
  if (answers.annual_fee === 'yes') input.willingToPayAnnualFee = true;

  return input;
}

export function toCashbackInput(answers: AnswerState): CashbackRecommendationsInput {
  const input: CashbackRecommendationsInput = { spendingCategories: toSpendingCategories(answers) };

  const userCreditScore = toUserCreditTier(answers);
  if (userCreditScore) input.userCreditScore = userCreditScore;

  const ownedCardIds = toOwnedCardIds(answers);
  if (ownedCardIds) input.ownedCardIds = ownedCardIds;

  if (answers.rewards_distribution === 'flat_rate') input.preferFlatRate = true;

  const cardBenefits = answers.card_benefits;
  if (isStringArray(cardBenefits)) {
    input.introAprImportant = cardBenefits.includes('intro_apr');
    input.welcomeBonusImportant = cardBenefits.includes('welcome_offer');
    if (cardBenefits.includes('no_annual_fee')) input.willingToPayAnnualFee = false;
  }

  if (answers.annual_fee === 'no') input.willingToPayAnnualFee = false;
  if (answers.annual_fee === 'yes') input.willingToPayAnnualFee = true;

  return input;
}

export function toBalanceTransferInput(answers: AnswerState): BalanceTransferRecommendationsInput {
  const input: BalanceTransferRecommendationsInput = {};

  const userCreditScore = toUserCreditTier(answers);
  if (userCreditScore) input.userCreditScore = userCreditScore;

  const ownedCardIds = toOwnedCardIds(answers);
  if (ownedCardIds) input.ownedCardIds = ownedCardIds;

  const debtIssuers = answers.debt_issuer;
  if (isStringArray(debtIssuers) && debtIssuers.length > 0) input.btDebtIssuers = debtIssuers;

  if (isString(answers.priority)) {
    const btPriority = BT_PRIORITY_MAP[answers.priority];
    if (btPriority) input.btPriority = btPriority;
  }
  if (isString(answers.rewards_importance)) {
    const btRewardsImportance = BT_REWARDS_IMPORTANCE_MAP[answers.rewards_importance];
    if (btRewardsImportance) input.btRewardsImportance = btRewardsImportance;
  }

  return input;
}

export function toSavingOnInterestInput(
  answers: AnswerState,
): SavingOnInterestRecommendationsInput {
  const input: SavingOnInterestRecommendationsInput = {};

  const userCreditScore = toUserCreditTier(answers);
  if (userCreditScore) input.userCreditScore = userCreditScore;

  const ownedCardIds = toOwnedCardIds(answers);
  if (ownedCardIds) input.ownedCardIds = ownedCardIds;

  if (isString(answers.priority)) {
    const savingInterestPriority = SAVING_INTEREST_PRIORITY_MAP[answers.priority];
    if (savingInterestPriority) input.savingInterestPriority = savingInterestPriority;
  }

  return input;
}

export function toBuildingCreditInput(answers: AnswerState): BuildingCreditRecommendationsInput {
  const input: BuildingCreditRecommendationsInput = {};

  const userCreditScore = toUserCreditTier(answers);
  if (userCreditScore) input.userCreditScore = userCreditScore;

  const ownedCardIds = toOwnedCardIds(answers);
  if (ownedCardIds) input.ownedCardIds = ownedCardIds;

  if (answers.annual_fee === 'no') input.willingToPayAnnualFee = false;
  if (answers.annual_fee === 'yes') input.willingToPayAnnualFee = true;

  if (answers.security_deposit === 'yes') input.willingToPayDeposit = true;
  if (answers.security_deposit === 'no') input.willingToPayDeposit = false;

  if (answers.student_status === 'yes') input.isStudent = true;
  if (answers.student_status === 'no') input.isStudent = false;

  return input;
}
